import JSZip from "jszip";
import { DEFAULT_GEOMETRY, labelCanvas } from "./artLabelPreviewMath";
import { plateRegionsPx } from "../serials/tags/layout/artGeometryPt";
import { methodLabel } from "./variationRank";

// Pacote "Baixar para editar" (spec 2026-10-01 §6): montado no navegador, sem rota no backend.
const BACKGROUND_FILE = "fundo-sem-personagem.png";
const FULL_FILE = "continuacao-completa.png";
const README_FILE = "LEIAME.txt";
const BLEED_FILL = "rgba(225,29,72,0.25)";
const TRIM_STROKE = "#e11d48";
const SAFE_STROKE = "#16a34a";
const PLATE_FILL = "rgba(100,116,139,0.45)";

const withDefaults = (geometry) => ({ ...DEFAULT_GEOMETRY, ...(geometry || {}) });
const sizeTag = (w, h) => `${w}x${h}`;
const variationFileName = (variation, canvas) => `variacao-${sizeTag(variation.width || canvas.width, variation.height || canvas.height)}.png`;
const guideFileName = (canvas) => `guia-${sizeTag(canvas.width, canvas.height)}.png`;
const cardFileName = (contentType, url) => {
    const hint = `${contentType || ""} ${url || ""}`.toLowerCase();
    if (/jpe?g/.test(hint)) return "carta.jpg";
    if (/webp/.test(hint)) return "carta.webp";
    return "carta.png";
};

export const kitZipName = (artKey) => `${String(artKey || "arte").replace(/[^a-z0-9]+/gi, "_")}-editar.zip`;

/** Retângulos das guias em px do canvas (largura = labelWidth + 2·bleed mm, origem no topo-esquerdo da sangria). */
export function guideRects({ canvas, geometry }) {
    const g = withDefaults(geometry);
    const pxPerMm = canvas.width / (g.labelWidth + 2 * g.bleed);
    const r = (x, y, w, h) => ({ x: Math.round(x * pxPerMm), y: Math.round(y * pxPerMm), width: Math.round(w * pxPerMm), height: Math.round(h * pxPerMm) });
    return {
        trim: r(g.bleed, g.bleed, g.labelWidth, g.frontHeight),
        safe: r(g.bleed + g.safeInset, g.bleed + g.safeInset, g.labelWidth - 2 * g.safeInset, g.frontHeight - 2 * g.safeInset),
        plates: plateRegionsPx(g, canvas.width),
    };
}

/** PNG-guia do mesmo tamanho da arte, fundo transparente. Devolve o base64 sem o prefixo data:. */
export function renderGuidePng({ canvas, geometry, createCanvas = () => document.createElement("canvas") }) {
    const { trim, safe, plates } = guideRects({ canvas, geometry });
    const el = createCanvas();
    el.width = canvas.width;
    el.height = canvas.height;
    const ctx = el.getContext("2d");
    if (!ctx) throw new Error("Este navegador não desenha canvas: o PNG-guia não pôde ser gerado");
    // sangria: faixa pintada fora da linha de corte
    ctx.fillStyle = BLEED_FILL;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(trim.x, trim.y, trim.width, trim.height);
    ctx.lineWidth = 2;
    ctx.setLineDash([]);
    ctx.strokeStyle = TRIM_STROKE;
    ctx.strokeRect(trim.x, trim.y, trim.width, trim.height);
    ctx.setLineDash([12, 8]);
    ctx.strokeStyle = SAFE_STROKE;
    ctx.strokeRect(safe.x, safe.y, safe.width, safe.height);
    ctx.fillStyle = PLATE_FILL;
    for (const p of Object.values(plates)) ctx.fillRect(p.x, p.y, p.width, p.height);
    return el.toDataURL("image/png").replace(/^data:image\/png;base64,/, "");
}

/** LEIAME.txt em pt-BR, com quebras CRLF (abre direito no Bloco de Notas). */
export function kitReadme({ art, variation, canvas, geometry, included, notes }) {
    const g = withDefaults(geometry);
    const lines = [
        `Arte da etiqueta: ${art?.cardName || ""}${art?.setName ? ` · ${art.setName}` : ""} (${art?.artKey || ""})`,
        `Variação: ${methodLabel(variation?.method)}`,
        "",
        "Arquivos",
        ...included.map(([name, what]) => `- ${name}: ${what}`),
        ...notes.map((n) => `- ${n}`),
        "",
        `Guias (${guideFileName(canvas)}, mesmo tamanho da arte, fundo transparente)`,
        `- Faixa vermelha: sangria de ${g.bleed} mm por lado; é cortada na impressão, não deixe nada importante nela.`,
        "- Linha vermelha: linha de corte.",
        `- Tracejado verde: área segura (${g.safeInset} mm para dentro do corte).`,
        "- Retângulos cinza: placas de texto (nome, condição, autógrafo e meta); evite detalhe forte embaixo delas.",
        "",
        "Entrega",
        `- PNG de exatamente ${canvas.width} × ${canvas.height} px, sem a camada de guias.`,
        "- Na revisão da arte, use \"Enviar arte corrigida\": a arte entra como nova variação, aparece no slab e passa pelo checklist antes de aprovar.",
        "",
    ];
    return lines.join("\r\n");
}

/**
 * Monta o ZIP. Só a variação é obrigatória; fundo e carta que faltam ou não baixam (CORS, URL
 * expirada) ficam fora e o LEIAME diz por quê.
 */
export async function buildEditKit({
    art, variation, backgroundUrl = null, cardImageUrl = null, geometry,
    fetchImpl = (...args) => fetch(...args), renderGuide = renderGuidePng, zipType = "blob",
}) {
    const g = withDefaults(geometry);
    const canvas = labelCanvas(g);
    const zip = new JSZip();
    const files = [];
    const included = [];
    const notes = [];
    const add = (name, data, what, options) => {
        zip.file(name, data, options);
        files.push(name);
        included.push([name, what]);
    };
    const get = async (url) => {
        const res = await fetchImpl(url);
        if (!res || !res.ok) throw new Error(`HTTP ${res ? res.status : "?"}`);
        const type = (res.headers && typeof res.headers.get === "function" && res.headers.get("content-type")) || "";
        return { bytes: await res.arrayBuffer(), type };
    };

    let main;
    try {
        main = await get(variation.url);
    } catch (err) {
        throw new Error(`Não foi possível baixar a variação (${err.message})`);
    }
    add(variationFileName(variation, canvas), main.bytes, "a variação selecionada na revisão; é o arquivo a editar.");

    // continuação: o canvas composto inteiro (etiqueta + ilustração continuada), só para referência
    if (variation.fullUrl) {
        try {
            add(FULL_FILE, (await get(variation.fullUrl)).bytes, "a continuação completa (etiqueta em cima, ilustração da carta continuada embaixo); referência de continuidade, não é para enviar.");
        } catch (_) {
            notes.push(`${FULL_FILE} não incluído: o navegador não conseguiu baixar o arquivo.`);
        }
    }

    if (!backgroundUrl) {
        notes.push(`${BACKGROUND_FILE} não incluído: este lote não separou o personagem.`);
    } else {
        try {
            add(BACKGROUND_FILE, (await get(backgroundUrl)).bytes, "o fundo do lote com o personagem apagado; ponto de partida limpo para retoques.");
        } catch (_) {
            notes.push(`${BACKGROUND_FILE} não incluído: o navegador não conseguiu baixar o arquivo.`);
        }
    }

    let cardOk = false;
    if (cardImageUrl) {
        try {
            const card = await get(cardImageUrl);
            add(cardFileName(card.type, cardImageUrl), card.bytes, "imagem da carta, para referência de cor e de continuidade com a etiqueta.");
            cardOk = true;
        } catch (_) {
            cardOk = false;
        }
    }
    if (!cardOk) notes.push("carta não incluída: imagem indisponível.");

    add(guideFileName(canvas), renderGuide({ canvas, geometry: g }), "guias de sangria, corte, área segura e placas; use como camada por cima e não exporte junto.", { base64: true });

    zip.file(README_FILE, kitReadme({ art, variation, canvas, geometry: g, included, notes }));
    files.push(README_FILE);

    return { blob: await zip.generateAsync({ type: zipType }), files, name: kitZipName(art?.artKey) };
}

/** Dispara o download no navegador (mesmo padrão do geometry.json antigo da revisão). */
export function downloadBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
